// Framework-agnostic helpers for the admin access-control surfaces (PRD-0004
// §3a Config + §3b Access Rights). Kept beside the contract types (like
// search.ts) so every surface labels roles/stages and reads grant warnings the
// same way. No React here — the apps wrap these in their own hooks/components.
import { ApiError } from './client';
import type { AccessRole, FunnelStage, GrantWarning, RoomAdminRole } from './types';

/**
 * Pull the advisory grant/role-change warnings out of a rejected request. The
 * grant and admin-role endpoints answer a 409 whose problem+json body carries a
 * top-level `warnings` array (advise + acknowledge, ADR-0010 §3). Returns [] for
 * any other error so callers can branch on `length`.
 */
export function grantWarningsFromError(error: unknown): GrantWarning[] {
  if (error instanceof ApiError && error.status === 409 && error.problem?.warnings) {
    return error.problem.warnings;
  }
  return [];
}

/** Human labels for the room admin role (ADR-0010 §1). */
export const ROOM_ADMIN_ROLE_LABELS: Record<RoomAdminRole, string> = {
  intermediary: 'Intermediary / broker',
  buyer: 'Buyer (buy-side principal)',
  seller: 'Seller (principal, no broker)',
};

/** One-line description of what each room role implies (for the Config picker). */
export const ROOM_ADMIN_ROLE_HINTS: Record<RoomAdminRole, string> = {
  intermediary: 'You are running the deal as the broker. Buyers you invite are guests.',
  buyer: 'You are a principal buyer running your own diligence, with no broker.',
  seller: 'You are a principal seller with no broker. Buyers you invite are guests.',
};

/** Human labels for the access (party) role on a grant (ADR-0010 §2). */
export const ACCESS_ROLE_LABELS: Record<AccessRole, string> = {
  seller_team: 'Seller team (own side)',
  intermediary_team: 'Intermediary team (own side)',
  diligence_team: 'Diligence team (counterparty)',
  custom: 'Custom (no default)',
};

/**
 * The access-role label for a room run with `adminRole`. In a BUYER-RUN room the
 * roles read from the buyer's side (ADR-0039 §"Buyer-run rooms"): the buyer's
 * diligence advisers are `diligence_team` — the buy-side team who ask the seller
 * questions — and the seller's own people are `seller_team`. Elsewhere the
 * standard labels apply.
 */
export function accessRoleLabel(role: AccessRole, adminRole: RoomAdminRole | null | undefined): string {
  if (adminRole === 'buyer') {
    if (role === 'diligence_team') return 'Your diligence team (QoE, attorney, lender)';
    if (role === 'seller_team') return "Seller's team";
  }
  return ACCESS_ROLE_LABELS[role];
}

/** Human labels for each funnel stage (PRD-0004 §1.1). */
export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  teaser: 'Teaser',
  nda_signed: 'NDA signed',
  cim: 'CIM',
  loi_submitted: 'LOI submitted',
  due_diligence: 'Due diligence',
  exclusivity_expires: 'Exclusivity expires',
  closing: 'Closing',
};

/** All room roles / access roles / funnel stages, in canonical order, for
 * building selectors without re-listing the unions per app. */
export const ROOM_ADMIN_ROLES: readonly RoomAdminRole[] = ['intermediary', 'buyer', 'seller'];
export const ACCESS_ROLES: readonly AccessRole[] = [
  'intermediary_team',
  'seller_team',
  'diligence_team',
  'custom',
];
export const FUNNEL_STAGES: readonly FunnelStage[] = [
  'teaser',
  'nda_signed',
  'cim',
  'loi_submitted',
  'due_diligence',
  'exclusivity_expires',
  'closing',
];
